import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ParsedData, ColumnSelection } from '../types';

interface PreprocessingState {
  missingValueMethod?: 'mean' | 'median' | 'forward-fill' | 'linear-interpolation' | 'remove';
  outlierHandling?: 'keep' | 'remove' | 'cap';
  resamplingFrequency?: 'daily' | 'weekly' | 'monthly';
  resamplingMethod?: 'mean' | 'sum' | 'count';
  transformation?: 'none' | 'log' | 'sqrt';
  differencing?: {
    order: number;
    enabled: boolean;
  };
  cleanedData?: any[];
  appliedChanges: string[];
  statisticalAnalysis?: {
    isStationary: boolean;
    hasSeasonality: boolean;
    hasTrend: boolean;
  };
}

interface DataState {
  // Raw uploaded data
  rawData: ParsedData | null;

  // Column selection for modeling
  columnSelection: ColumnSelection;

  // Multivariate mode flag
  isMultivariate: boolean;

  // Exogenous features for multivariate forecasting
  exogenousFeatures: string[];

  // Preprocessing state
  preprocessing: PreprocessingState;

  // Processed data ready for WASM
  processedData: {
    timestamps: number[];
    values: number[];
    exogenousData?: number[][]; // For multivariate forecasting
  } | null;

  // Actions
  setRawData: (data: ParsedData | null) => void;
  setColumnSelection: (selection: Partial<ColumnSelection>) => void;
  setMultivariateMode: (isMultivariate: boolean) => void;
  setExogenousFeatures: (features: string[]) => void;
  setPreprocessing: (preprocessing: Partial<PreprocessingState>) => void;
  setProcessedData: (data: { timestamps: number[]; values: number[]; exogenousData?: number[][] } | null) => void;
  clearData: () => void;
}

export const useDataStore = create<DataState>()(
  devtools(
    set => ({
      rawData: null,
      columnSelection: {
        dateColumn: null,
        targetColumn: null,
        regressorColumns: [],
      },
      isMultivariate: false,
      exogenousFeatures: [],
      preprocessing: {
        appliedChanges: [],
      },
      processedData: null,

      setRawData: data => set({ rawData: data }),

      setColumnSelection: selection =>
        set(state => ({
          columnSelection: { ...state.columnSelection, ...selection },
        })),

      setMultivariateMode: isMultivariate => set({ isMultivariate }),

      setExogenousFeatures: features => set({ exogenousFeatures: features }),

      setPreprocessing: preprocessing =>
        set(state => ({
          preprocessing: { ...state.preprocessing, ...preprocessing },
        })),

      setProcessedData: data => set({ processedData: data }),

      clearData: () =>
        set({
          rawData: null,
          columnSelection: {
            dateColumn: null,
            targetColumn: null,
            regressorColumns: [],
          },
          isMultivariate: false,
          exogenousFeatures: [],
          preprocessing: {
            appliedChanges: [],
          },
          processedData: null,
        }),
    }),
    { name: 'DataStore' }
  )
);
